import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Icon, Paper, Tooltip, Typography } from '@mui/material';
import { DataGrid, GridActionsColDef, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import { Organisatie } from '../../../models/organisatie/organisatie';
import { ListParams } from '../../../models/listParams';

import ProgressBar from '../../controls/ProgressBar';
import ErrorMessage from '../../controls/ErrorMessage';
import DialogStartSharingOrganisatie from '../../controls/dialogs/DialogStartSharingOrganisatie';

import SharedOrganisatiesActionMenu from './SharedOrganisatiesActionMenu';

import { planManagementService } from '../../../services/plan-management.service';
import { authService } from '../../../services/auth.service';
import { plannenService } from '../../../services/plannen.service';
import { pageSizeStorageKey } from '../../../services/utils/localStorageKeys';
import { usePersistedPageSize } from '../../../hooks/usePersistedPageSize';


const CustomNoRowsOverlay = () => {
  return (
    <Paper
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
        width: "100%",
        padding: 2,
        backgroundColor: "transparent",
      }}
      elevation={0}
    >
      <Typography variant="body1">Niets gevonden</Typography>
    </Paper>
  );
};

interface SharedOrganisatiesProps {
  isZonderGebied: boolean; // Callback function to trigger refresh
}

const SharedOrganisaties: React.FC<SharedOrganisatiesProps> = ({ 
  isZonderGebied
}) => {
  const navigate = useNavigate();
  const [organisaties, setOrganisaties] = useState<Organisatie[]>([]);
  const [isAdmin, setAdmin] = useState<boolean>(false);
 
  const [pageIndex, setPageIndex] = useState(0);
  const { pageSize, setPageSize } = usePersistedPageSize(pageSizeStorageKey('shared.organisaties'));
  const [totalCount, setTotalCount] = useState(0);

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  const [open, setOpenDialog] = useState(false);

  const setIsAdmin = async () => {
    const isAdmin = await authService.isAdmin();
    setAdmin(isAdmin);
  }

  const fetchOrganisaties = useCallback(async () => {
    setLoading(true);
    try {
      const params: ListParams = {
        offset: pageIndex * pageSize, 
        limit: pageSize,
        directSharedOnly: isZonderGebied
      };

      let data = [];

      let organisaties: any[] = [];
      if (isZonderGebied) {
        data = await plannenService.getOrganisatiesSharedPlans(params);
        organisaties = data.items;
      }
      else {
        data = await planManagementService.getSharedOrganisaties(params);
        organisaties = data.items;
      }

      setOrganisaties(organisaties);
      setTotalCount(data.count);
      setLoading(false);
    }
    catch (error: any) {
      if (error.message === 'Unable to refresh token') {
        navigate('/verlopen');
      }
      setError(error.message || 'Er is een fout opgetreden');
      setLoading(false);
    }
  }, [pageIndex, pageSize, isZonderGebied, navigate]);

  const gridColumns = useMemo<(GridColDef<Organisatie> | GridActionsColDef<Organisatie>)[]>(() => {
    const baseColumns: (GridColDef<Organisatie> | GridActionsColDef<Organisatie>)[] = [
      { field: 'id', headerName: 'Id', flex: 2 },
      { field: 'displayName', headerName: 'Naam', flex: 3 },
    ];

    if (isZonderGebied) {
      baseColumns.push({ field: 'planCount', headerName: 'Aantal plannen', flex: 2 });
    }
  
    if (!isZonderGebied) {
      baseColumns.push({ field: 'organizationType', headerName: 'Type', flex: 2 });
    }

    baseColumns.push({
      field: 'actions',
      headerName: '',
      width: 70,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<Organisatie>) => (
        <SharedOrganisatiesActionMenu params={params} isZonderGebied={isZonderGebied} onOrganisatiesUpdate={fetchOrganisaties} />
      ),
    });
  
    return !isAdmin ? baseColumns.filter((col) => col.field !== "id") : baseColumns;
  }, [fetchOrganisaties, isAdmin, isZonderGebied]);

  useEffect(() => {
    setIsAdmin();
    setReady(true);
  }, []);

  useEffect(() => {
    if (!ready) return;
    fetchOrganisaties();
  }, [ready, fetchOrganisaties]);

  const onClickOpenDialogAddOrganisatie = (open: boolean = false) => {
    setOpenDialog(open);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleConfirmStartSharing = () => {
    fetchOrganisaties();
    handleCloseDialog();
  };

  const onChangePagination = async ({ page, pageSize }: { page: number, pageSize: number }) => {
    setPageIndex(page);
    setPageSize(pageSize);
  };

  return (
    <>
      { !isZonderGebied && authService.isKeyUser() && (
        <Tooltip title="Organisatie toevoegen" placement="right" arrow>
          <Button variant="text" color="secondary" onClick={ () => onClickOpenDialogAddOrganisatie(true) }>
            <Icon>add</Icon>
            Organisatie
          </Button>
        </Tooltip>
      )}

      <div className="grid-responsive">
        <DataGrid
          rows={organisaties}
          columns={gridColumns}
          disableColumnFilter
          disableColumnSorting 
          disableRowSelectionOnClick
          loading={false}
          rowCount={totalCount}
          paginationMode="server"
          pageSizeOptions={[5, 10, 25, 50, 100]}
          initialState={{
            pagination: {
              paginationModel: {
                page: pageIndex,
                pageSize: pageSize
              }
            }
          }}
          onPaginationModelChange={onChangePagination}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            loadingOverlay: () => null // or your own minimal custom component
          }}
          // localeText={{
          //   noRowsLabel: "Niets gevonden",
          // slots={{
          //   noRowsOverlay: CustomNoRowsOverlay,
          // }}
          sx={{
            border: 0,
          }}
        />
      </div>

      <DialogStartSharingOrganisatie
        open={open}
        title="Organisatie toevoegen"
        contentText="Kies één of meerdere beschikbare organisaties waarmee u uw plannen wilt delen en druk op Start met delen."
        sharing="gebied"
        allowMultiple
        groupBySource
        excludedOrganizationIds={organisaties.map((item) => item.id).filter((id): id is string => Boolean(id))}
        onClose={handleCloseDialog}
        onSubmit={handleConfirmStartSharing}/>

      { loading && (
        <ProgressBar />
      )}

      { error && (
        <ErrorMessage message={error} />
      )}

    </>
  );
};

export default SharedOrganisaties;
